



























































































import Vue, { PropType } from "vue";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "@vue/composition-api";
import GaIcon from "../../card-items/GaIcon.vue";
import { useGenAiPalette } from "../../data/design";

export interface ChipLevel {
  key: string;
  label: string;
  /** "⇄" for risk, the initial for sensitivity */
  mark: string;
  /** trigger chip: the soft tint (risk) or the solid colour (sensitivity) */
  trigger: { bg: string; text: string };
  /** checklist chip: always solid */
  menu: { bg: string; text: string };
}

export default Vue.extend({
  components: { GaIcon },
  props: {
    label: { type: String, required: true },
    levels: { type: Array as PropType<ChipLevel[]>, required: true },
    /** the applied selection, by level key */
    value: { type: Array as PropType<string[]>, required: true },
    /** the sensitivity list sits tighter and wider (w-52) */
    compact: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const T = useGenAiPalette();
    const root = ref<HTMLElement | null>(null);
    const open = ref(false);
    const applied = computed(() => props.value as string[]);
    const draft = ref<string[]>([...applied.value]);
    watch(applied, (v) => (draft.value = [...v]), { lazy: true });

    const toggle = () => {
      open.value = !open.value;
      if (open.value) draft.value = [...applied.value];
    };

    /** the last level cannot be switched off — an empty filter matches nothing */
    const flip = (key: string) => {
      const on = draft.value.includes(key);
      if (on && draft.value.length === 1) return;
      draft.value = on ? draft.value.filter((k) => k !== key) : [...draft.value, key];
    };

    const apply = () => {
      emit("change", [...draft.value]);
      open.value = false;
    };

    // closing without Apply drops the draft, so the trigger never shows a
    // selection the list is not using
    const on_outside = (e: MouseEvent) => {
      if (open.value && root.value && !root.value.contains(e.target as Node)) {
        open.value = false;
        draft.value = [...applied.value];
      }
    };
    onMounted(() => document.addEventListener("mousedown", on_outside));
    onBeforeUnmount(() => document.removeEventListener("mousedown", on_outside));

    const chip_style = (lvl: ChipLevel, on: boolean, where: "trigger" | "menu") => {
      if (!on) {
        return {
          background: T.value.ev.chipOffBg,
          color: T.value.ev.chipOffText,
          opacity: 0.25,
          fontSize: where === "menu" ? "10px" : lvl.mark === "⇄" ? "9.5px" : "8px",
        };
      }
      const c = where === "menu" ? lvl.menu : lvl.trigger;
      return {
        background: c.bg,
        color: c.text,
        fontSize: where === "menu" ? "10px" : lvl.mark === "⇄" ? "9.5px" : "8px",
      };
    };

    const summary = computed(() =>
      (props.levels as ChipLevel[])
        .filter((l) => applied.value.includes(l.key))
        .map((l) => l.label)
        .join(", ")
    );

    const field = computed(() => ({
      gap: "12px",
      height: "34px",
      padding: "0 10px",
      background: T.value.ev.inputBg,
      border: "1px solid " + T.value.ev.inputBorder,
      borderRadius: "6px",
      "--ga-field-hover": T.value.ev.inputBorderHover,
    }));

    return {
      T,
      root,
      open,
      applied,
      draft,
      toggle,
      flip,
      apply,
      chip_style,
      summary,
      field,
      menu_width: props.compact ? "208px" : "176px",
    };
  },
});
